import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  writeBatch,
  serverTimestamp,
  type Timestamp,
} from "firebase/firestore"
import { db } from "../firebase/config"

export interface Banner {
  id: string
  title?: string
  subtitle?: string
  description?: string
  image: string
  ctaText?: string
  ctaLink?: string
  isActive: boolean
  order: number
  type: "image-only" | "text-with-image"
  createdAt: string
  updatedAt: string
}

export interface BannerFormData {
  title?: string
  subtitle?: string
  description?: string
  image: string
  ctaText?: string
  ctaLink?: string
  isActive: boolean
  type: "image-only" | "text-with-image"
}

class BannerService {
  private collectionName = "banners"

  // Convert Firestore timestamp to ISO string
  private convertTimestamp(timestamp: Timestamp | string | undefined): string {
    if (!timestamp) return new Date().toISOString()
    if (typeof timestamp === "string") return timestamp
    return timestamp.toDate().toISOString()
  }

  // Get all banners ordered by order field
  async getBanners(): Promise<Banner[]> {
    try {
      const bannersRef = collection(db, this.collectionName)
      const q = query(bannersRef, orderBy("order", "asc"))
      const querySnapshot = await getDocs(q)

      return querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          title: data.title || "",
          subtitle: data.subtitle || "",
          description: data.description || "",
          image: data.image || "",
          ctaText: data.ctaText || "Shop Now",
          ctaLink: data.ctaLink || "/categories",
          isActive: Boolean(data.isActive),
          order: data.order || 0,
          type: data.type || "image-only",
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
        }
      })
    } catch (error) {
      console.error("Error fetching banners:", error)
      throw new Error("Failed to fetch banners")
    }
  }

  // Get only active banners for homepage display
  async getActiveBanners(): Promise<Banner[]> {
    try {
      const bannersRef = collection(db, this.collectionName)
      const q = query(bannersRef, where("isActive", "==", true), orderBy("order", "asc"))
      const querySnapshot = await getDocs(q)

      return querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          title: data.title || "",
          subtitle: data.subtitle || "",
          description: data.description || "",
          image: data.image || "",
          ctaText: data.ctaText || "Shop Now",
          ctaLink: data.ctaLink || "/categories",
          isActive: Boolean(data.isActive),
          order: data.order || 0,
          type: data.type || "image-only",
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
        }
      })
    } catch (error) {
      console.error("Error fetching active banners:", error)
      throw new Error("Failed to fetch active banners")
    }
  }

  // Create a new banner
  async createBanner(bannerData: BannerFormData): Promise<string> {
    try {
      // Get current banner count to set order
      const banners = await this.getBanners()
      const nextOrder = banners.length + 1

      const docRef = await addDoc(collection(db, this.collectionName), {
        ...bannerData,
        order: nextOrder,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })

      return docRef.id
    } catch (error) {
      console.error("Error creating banner:", error)
      throw new Error("Failed to create banner")
    }
  }

  // Update an existing banner
  async updateBanner(id: string, bannerData: Partial<BannerFormData>): Promise<void> {
    try {
      const bannerRef = doc(db, this.collectionName, id)
      await updateDoc(bannerRef, {
        ...bannerData,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating banner:", error)
      throw new Error("Failed to update banner")
    }
  }

  // Delete a banner
  async deleteBanner(id: string): Promise<void> {
    try {
      const bannerRef = doc(db, this.collectionName, id)
      await deleteDoc(bannerRef)

      // Reorder remaining banners
      await this.reorderAfterDelete()
    } catch (error) {
      console.error("Error deleting banner:", error)
      throw new Error("Failed to delete banner")
    }
  }

  // Reorder banners after deletion
  private async reorderAfterDelete(): Promise<void> {
    try {
      const banners = await this.getBanners()
      const batch = writeBatch(db)

      banners.forEach((banner, index) => {
        const bannerRef = doc(db, this.collectionName, banner.id)
        batch.update(bannerRef, {
          order: index + 1,
          updatedAt: serverTimestamp(),
        })
      })

      await batch.commit()
    } catch (error) {
      console.error("Error reordering banners:", error)
      throw new Error("Failed to reorder banners")
    }
  }

  // Reorder banners based on new order
  async reorderBanners(bannerIds: string[]): Promise<void> {
    try {
      const batch = writeBatch(db)

      bannerIds.forEach((bannerId, index) => {
        const bannerRef = doc(db, this.collectionName, bannerId)
        batch.update(bannerRef, {
          order: index + 1,
          updatedAt: serverTimestamp(),
        })
      })

      await batch.commit()
    } catch (error) {
      console.error("Error reordering banners:", error)
      throw new Error("Failed to reorder banners")
    }
  }
}

export const bannerService = new BannerService()
